import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import WhatsAppButton from "@/components/WhatsAppButton";
import { CommunityMap } from "@/components/PropertyMap";
import { AREA_POIS } from "@/data/areaPois";
import { breadcrumbSchema, faqSchema, placeSchema } from "@/lib/schema";

const POI_LEGEND = [
  { key: "school", label: "Schools", color: "#3B82F6" },
  { key: "hospital", label: "Hospitals", color: "#EF4444" },
  { key: "metro", label: "Metro", color: "#10B981" },
  { key: "mall", label: "Malls", color: "#F59E0B" },
  { key: "landmark", label: "Landmarks", color: "#001588" },
];

export default function AreaDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    (async () => {
      // Use the rich /communities endpoint so we get off-plan/ready projects,
      // developers, related blogs and agents alongside the area doc.
      try {
        const c = await api.get(`/communities/${slug}`);
        setA(c.data);
        setListings(c.data.listings || []);
      } catch {
        const area = await api.get(`/areas/${slug}`);
        setA(area.data);
        const l = await api.get("/listings", { params: { community: area.data.name, limit: 6 } });
        setListings(l.data.items);
      }
    })();
  }, [slug]);

  if (!a) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Communities", path: "/areas" },
    { name: a.name, path: `/areas/${a.slug}` },
  ];
  const faqs = [
    { q: `What is the average price per sqft in ${a.name}?`, a: a.average_price_apartment ? `Apartments in ${a.name} currently transact at around AED ${a.average_price_apartment}/sqft.` : `Prices in ${a.name} vary widely. Ask a Point Penta advisor for a bespoke comparable market analysis.` },
    { q: `What is the rental yield in ${a.name}?`, a: a.rental_yield ? `Average gross yield in ${a.name} is around ${a.rental_yield}%.` : "Yields depend on unit type and view. Contact us for community-specific data." },
    { q: `Is ${a.name} good for families?`, a: `${a.name} is served by top-tier schools and healthcare. See lifestyle section above for details.` },
    { q: `How do I view a property in ${a.name}?`, a: "Browse the current mandates below or contact us — most viewings arranged within 24 hours." },
  ];
  const pois = AREA_POIS[a.slug] || [];

  return (
    <div data-testid={`area-detail-${slug}`}>
      <SEO
        title={`${a.name} — community guide`}
        description={`${a.tagline || ""} ${(a.intro || a.description || "").slice(0, 100)}`}
        path={`/areas/${a.slug}`}
        image={a.hero_image}
        schema={[breadcrumbSchema(crumbs), faqSchema(faqs), placeSchema(a)]}
      />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent pointer-events-none"></div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pb-16 w-full text-white" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}>
          <Breadcrumbs items={crumbs} />
          <div className="overline text-[#001588] mb-4">Community guide · {a.name}</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-4xl mb-4 text-white">{a.name}</h1>
          <p className="text-white/85 text-xl max-w-2xl italic font-serif">{a.tagline}</p>
        </div>
      </section>

      <section className="py-16 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 space-y-8">
            <div>
              <div className="overline text-[#001588] mb-3">Intro</div>
              <p className="text-neutral-700 leading-relaxed text-lg">{a.intro || a.description}</p>
            </div>
            <div>
              <div className="overline text-[#001588] mb-3">Lifestyle</div>
              <p className="text-neutral-700 leading-relaxed">{a.lifestyle}</p>
            </div>
            {a.connectivity && <div><div className="overline text-[#001588] mb-3">Connectivity</div><p className="text-neutral-700 leading-relaxed">{a.connectivity}</p></div>}
            {Array.isArray(a.schools) && a.schools.length > 0 && <div><div className="overline text-[#001588] mb-3">Nearby schools</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.schools.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {typeof a.schools === "string" && a.schools && <div><div className="overline text-[#001588] mb-3">Nearby schools</div><p className="text-neutral-700 leading-relaxed">{a.schools}</p></div>}
            {Array.isArray(a.hospitals) && a.hospitals.length > 0 && <div><div className="overline text-[#001588] mb-3">Nearby hospitals</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.hospitals.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {Array.isArray(a.metro_access) && a.metro_access.length > 0 && <div><div className="overline text-[#001588] mb-3">Metro / bus access</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.metro_access.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {Array.isArray(a.malls) && a.malls.length > 0 && <div><div className="overline text-[#001588] mb-3">Shopping malls</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.malls.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {Array.isArray(a.beaches) && a.beaches.length > 0 && <div><div className="overline text-[#001588] mb-3">Beaches</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.beaches.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {Array.isArray(a.parks) && a.parks.length > 0 && <div><div className="overline text-[#001588] mb-3">Parks</div><ul className="text-neutral-700 leading-relaxed space-y-1">{a.parks.map((s) => <li key={s}>· {s}</li>)}</ul></div>}
            {a.investment_analysis && <div><div className="overline text-[#001588] mb-3">Investment analysis</div><p className="text-neutral-700 leading-relaxed">{a.investment_analysis}</p></div>}
          </div>
          <aside className="md:col-span-4">
            <div className="border border-neutral-200 p-6 space-y-4">
              <div className="overline text-[#001588]">Market snapshot</div>
              {a.average_price_apartment && <Stat label="Avg. apartment price" value={`AED ${a.average_price_apartment}/sqft`} />}
              {a.average_price_villa && <Stat label="Avg. villa price" value={`AED ${a.average_price_villa}/sqft`} />}
              {a.average_rent_apartment && <Stat label="Avg. rent (1BR)" value={`AED ${Number(a.average_rent_apartment * 1000).toLocaleString()}/yr`} />}
              {a.rental_yield && <Stat label="Rental yield" value={`${a.rental_yield}%`} />}
              {a.capital_appreciation && <Stat label="Capital appreciation" value={`${a.capital_appreciation}%`} />}
              {a.investment_score && <Stat label="Investment score" value={`${a.investment_score}/10`} />}
              {a.family_score && <Stat label="Family score" value={`${a.family_score}/10`} />}
              {a.luxury_score && <Stat label="Luxury score" value={`${a.luxury_score}/10`} />}
              {a.metrics_source && (
                <div className="pt-3 mt-3 border-t border-neutral-200 text-[10px] tracking-[0.15em] uppercase text-neutral-500" data-testid="community-metrics-source">
                  {a.metrics_source === "live" && (
                    <>Live data · {(a.metrics_sample_sale || 0) + (a.metrics_sample_rent || 0)} Point Penta mandates</>
                  )}
                  {a.metrics_source === "hybrid" && (
                    <>Blended · {(a.metrics_sample_sale || 0) + (a.metrics_sample_rent || 0)} mandates + Point Penta research desk</>
                  )}
                  {a.metrics_source === "editorial" && (
                    <>Point Penta research desk · Q1 2026</>
                  )}
                </div>
              )}
            </div>
            {a.amenities?.length > 0 && (
              <div className="border border-neutral-200 p-6 mt-4">
                <div className="overline text-[#001588] mb-3">Amenities</div>
                <ul className="space-y-2 text-sm text-neutral-700">
                  {a.amenities.map((am) => <li key={am}>· {am}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4"><WhatsAppButton className="w-full" message={`Hi Point Penta, I'm interested in ${a.name}.`} testId={`whatsapp-area-${a.slug}`} /></div>
          </aside>
        </div>
      </section>

      {/* Community map with POIs */}
      {pois.length > 0 && (
        <section className="py-16 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="overline text-[#001588] mb-3">The map</div>
                <h2 className="font-serif text-4xl">Around {a.name}</h2>
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs">
                {POI_LEGEND.map((l) => (
                  <div key={l.key} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full border-2 border-neutral-300" style={{ background: l.color }}></span>
                    <span className="text-neutral-600">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <CommunityMap community={a.name} pois={pois} height={480} />
          </div>
        </section>
      )}

      {/* Projects — off-plan + ready */}
      {Array.isArray(a.off_plan_projects) && a.off_plan_projects.length > 0 && (
        <section className="py-16 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="overline text-[#001588] mb-3">Off-plan projects</div>
            <h2 className="font-serif text-4xl mb-8">New in {a.name}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {a.off_plan_projects.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/projects/${p.slug}`} className="border border-neutral-200 bg-white block group" data-testid={`area-offplan-${p.slug}`}>
                  {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-52 object-cover" loading="lazy" />}
                  <div className="p-5">
                    <div className="overline text-[#001588] mb-1">{p.status} · {p.handover || "TBA"}</div>
                    <div className="font-serif text-xl group-hover:text-[#001588]">{p.name}</div>
                    {p.starting_price && <div className="text-sm text-neutral-600 mt-1">from AED {Number(p.starting_price).toLocaleString()}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {Array.isArray(a.ready_projects) && a.ready_projects.length > 0 && (
        <section className="py-16 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="overline text-[#001588] mb-3">Ready projects</div>
            <h2 className="font-serif text-4xl mb-8">Move-in ready in {a.name}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {a.ready_projects.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/projects/${p.slug}`} className="border border-neutral-200 bg-white block group" data-testid={`area-ready-${p.slug}`}>
                  {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-52 object-cover" loading="lazy" />}
                  <div className="p-5">
                    <div className="overline text-[#001588] mb-1">{p.status}</div>
                    <div className="font-serif text-xl group-hover:text-[#001588]">{p.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {Array.isArray(a.developers) && a.developers.length > 0 && (
        <section className="py-16 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="overline text-[#001588] mb-3">Developers</div>
            <h2 className="font-serif text-4xl mb-8">Active in {a.name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {a.developers.map((d) => (
                <Link key={d.id} to={`/developers/${d.slug}`} className="border border-neutral-200 bg-white p-4 flex items-center justify-center h-24 text-center hover:border-[#001588]" data-testid={`area-dev-${d.slug}`}>
                  <span className="font-serif text-sm">{d.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {Array.isArray(a.related_blogs) && a.related_blogs.length > 0 && (
        <section className="py-16 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="overline text-[#001588] mb-3">Journal</div>
            <h2 className="font-serif text-4xl mb-8">Related insights</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {a.related_blogs.map((b) => (
                <Link key={b.slug} to={`/journal/${b.slug}`} className="border border-neutral-200 bg-white block group">
                  {b.hero_image && <img src={b.hero_image} alt={b.title} className="w-full h-40 object-cover" loading="lazy" />}
                  <div className="p-4"><div className="font-serif text-lg group-hover:text-[#001588]">{b.title}</div></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 border-b border-neutral-200">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <h2 className="font-serif text-4xl mb-8">Living in {a.name}</h2>
          <div className="border-t border-neutral-200">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-neutral-200 py-5" data-testid={`area-faq-${i}`}>
                <div className="font-serif text-lg text-neutral-900 mb-2">{f.q}</div>
                <div className="text-neutral-700 leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {listings.length > 0 && (
        <section className="py-16">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-serif text-4xl">Available in {a.name}</h2>
              <Link to={`/properties?community=${encodeURIComponent(a.name)}`} className="text-sm text-neutral-600 hover:text-[#001588]">View all →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-14">
              {listings.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-baseline justify-between border-b border-neutral-200 pb-3">
      <span className="text-xs uppercase tracking-wider text-neutral-500">{label}</span>
      <span className="font-serif text-lg text-neutral-900">{value}</span>
    </div>
  );
}
